import { Combobox, type ComboboxOption } from '@/components/Combobox';
import { MultiCombobox } from '@/components/MultiCombobox';
import { Input } from '@/components/Input';
import { useCluvioFilterOptions } from '@/api/hooks';

interface CluvioOptionInputProps {
  /** Cluvio filter ID. When absent, falls back to a plain text input. */
  filterId?: string | null;
  /** Predefined vocabulary (e.g. timerange presets). Skips the options query. */
  presets?: ComboboxOption[];
  value?: ComboboxOption;
  onChange: (option: ComboboxOption) => void;
  placeholder?: string;
}

/**
 * Value input that suggests the real options of a Cluvio filter
 * (/cluvio/filters/:id/options) — or a predefined vocabulary for date-range
 * and aggregation filters — while still allowing arbitrary free text.
 */
export function CluvioOptionInput({ filterId, presets, value, onChange, placeholder }: CluvioOptionInputProps) {
  const { data, isLoading } = useCluvioFilterOptions(presets ? '' : (filterId ?? ''));

  if (presets) {
    return (
      <Combobox
        value={value?.label ?? ''}
        onChange={onChange}
        options={presets}
        placeholder={placeholder ?? 'Select a preset or type a custom value…'}
      />
    );
  }

  if (!filterId) {
    return (
      <Input
        value={value?.label ?? ''}
        onChange={(e) => onChange({ label: e.target.value, value: e.target.value })}
        placeholder={placeholder}
      />
    );
  }

  return (
    <Combobox
      value={value?.label ?? ''}
      onChange={onChange}
      options={data ?? []}
      loading={isLoading}
      placeholder={placeholder ?? 'Type to search Cluvio options…'}
    />
  );
}

interface CluvioMultiOptionInputProps {
  filterId?: string | null;
  presets?: ComboboxOption[];
  values: ComboboxOption[];
  onChange: (values: ComboboxOption[]) => void;
  placeholder?: string;
}

/** Multi-value variant: suggests a Cluvio filter's options as chips. */
export function CluvioMultiOptionInput({ filterId, presets, values, onChange, placeholder }: CluvioMultiOptionInputProps) {
  const { data, isLoading } = useCluvioFilterOptions(presets ? '' : (filterId ?? ''));
  return (
    <MultiCombobox
      values={values}
      onChange={onChange}
      options={presets ?? data ?? []}
      loading={isLoading}
      placeholder={placeholder ?? 'Add values…'}
    />
  );
}
